<template>
  <!-- Nuxt UI boundary: URadioGroup's card variant renders button-role radios, not native radios. -->
  <label
    :aria-label="accessibleName"
    :class="[
      structure['root'],
      skin['root'],
      interaction['root'],
      borderTone === 'strong' ? skin['root-strong'] : '',
      hasError ? skin['root-error'] : '',
      isSelected ? skin['root-selected'] : ''
    ]"
  >
    <VisuallyHidden>
      <input
        v-model="selected"
        type="radio"
        :name="name"
        :value="value"
      />
    </VisuallyHidden>

    <span :class="structure['body']">
      <span
        :class="[
          structure['title'],
          skin['title'],
          titleSize === 'md' ? structure['title-md'] : structure['title-sm']
        ]"
        >{{ title }}</span
      >

      <span :class="[structure['description'], skin['description']]">{{ description }}</span>
    </span>

    <CheckCircleIcon
      :class="[structure['check'], skin['check'], isSelected ? '' : skin['check-hidden']]"
      aria-hidden="true"
    />
  </label>
</template>

<script setup lang="ts">
import { CheckCircleIcon } from '@heroicons/vue/24/outline'
import { computed } from 'vue'

import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden.atom.vue'

import structure from './RadioOptionCard.structure.module.css'
import skin from './RadioOptionCard.skin.module.css'
import interaction from './RadioOptionCard.interaction.module.css'

const props = withDefaults(
  defineProps<{
    /** Accessible name of the whole card. */
    accessibleName: string
    /** Card border emphasis in dark mode: `subtle` is a faint white edge, `strong` a solid gray one. */
    borderTone?: 'subtle' | 'strong'
    /** Supporting line under the title. */
    description: string
    /** Marks the card as part of a group with a validation error. */
    hasError?: boolean
    /** Radio group name shared by the cards that choose between the same options. */
    name: string
    /** Title text size. */
    titleSize?: 'sm' | 'md'
    /** Option name shown on the card. */
    title: string
    /** The option this card selects. */
    value: string
  }>(),
  { borderTone: 'subtle', hasError: false, titleSize: 'sm' }
)

/** The selected option; this card is checked when it equals `value`. */
const selected = defineModel<string>({ required: true })

const isSelected = computed(() => selected.value === props.value)
</script>
